import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/agency/Dashboard";
import Marketplace from "@/pages/agency/Marketplace";
import LeadDetail from "@/pages/agency/LeadDetail";
import MyLeads from "@/pages/agency/MyLeads";
import Billing from "@/pages/agency/Billing";
import Profile from "@/pages/Profile";
import Analytics from "@/pages/admin/Analytics";
import AdminLeads from "@/pages/admin/Leads";
import ImportCsv from "@/pages/admin/ImportCsv";
import Agencies from "@/pages/admin/Agencies";
import Payments from "@/pages/admin/Payments";
import Subscriptions from "@/pages/admin/Subscriptions";
import Plans from "@/pages/admin/Plans";
import Settings from "@/pages/admin/Settings";
import AuditLogs from "@/pages/admin/AuditLogs";

const home = (u) => (u.role === "super_admin" ? "/admin" : "/app");

const Loading = () => <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;

const RequireRole = ({ role }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={home(user)} replace />;
  return <AppShell />;
};

const GuestOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to={home(user)} replace /> : children;
};

const Root = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return <Navigate to={user ? home(user) : "/login"} replace />;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Root />} />
              <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/app" element={<RequireRole role="agency" />}>
                <Route index element={<Dashboard />} />
                <Route path="marketplace" element={<Marketplace />} />
                <Route path="leads/:id" element={<LeadDetail />} />
                <Route path="my-leads" element={<MyLeads />} />
                <Route path="billing" element={<Billing />} />
                <Route path="profile" element={<Profile />} />
              </Route>
              <Route path="/admin" element={<RequireRole role="super_admin" />}>
                <Route index element={<Analytics />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="import" element={<ImportCsv />} />
                <Route path="agencies" element={<Agencies />} />
                <Route path="payments" element={<Payments />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="plans" element={<Plans />} />
                <Route path="settings" element={<Settings />} />
                <Route path="audit" element={<AuditLogs />} />
                <Route path="profile" element={<Profile />} />
              </Route>
              <Route path="*" element={<Root />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
